import { useSelector } from 'react-redux'
import { listDomains } from 'account/components/userManagement/domains/actions'
import {
  listAllDomainTenants,
  listTenants,
} from 'account/components/userManagement/tenants/new-actions'
import {
  allDomainTenantsById,
  allDomainTenantsSelector,
  tenantsById,
  tenantsSelector,
} from 'account/components/userManagement/tenants/selectors'
import type { RootState } from 'app/store'
import useListAction from 'core/hooks/useListAction'
import { sessionStoreKey } from 'core/session/sessionReducers'
import { canManageAllDomains } from 'core/utils/rbac'

// See useClusterAccessTenants.spec.md (UCAT-FR-001 .. UCAT-FR-003) and
// ClusterAccessField.spec.md (CAF-FR-002): tenant source for cluster access (sharing).
// System-scoped users (platform_admin, sp_admin, default-domain admin) can share a cluster with
// tenants from ANY domain, so they read the all-domain list. Everyone else keeps the tenants
// visible within their own domain.
export default function useClusterAccessTenants() {
  // Subscribe to role changes so the source re-evaluates when the session (re)hydrates
  useSelector((state: RootState) => state[sessionStoreKey]?.userDetails?.role)
  const isSystemScoped = canManageAllDomains()

  const { loading: loadingAllDomains, reload: reloadAllDomains } = useListAction(
    listAllDomainTenants,
    { preRequisites: isSystemScoped },
  )
  const { loading: loadingOwnDomain, reload: reloadOwnDomain } = useListAction(listTenants, {
    preRequisites: !isSystemScoped,
  })
  // The Domain column / tooltip suffix resolves names from the Domains cache
  useListAction(listDomains)
  const allDomainTenants = useSelector(allDomainTenantsSelector)
  const ownDomainTenants = useSelector(tenantsSelector)
  // Memoized shared id maps, so rows that call this hook don't each rebuild one
  const allDomainById = useSelector(allDomainTenantsById)
  const ownDomainById = useSelector(tenantsById)

  return {
    tenants: isSystemScoped ? allDomainTenants : ownDomainTenants,
    tenantsById: isSystemScoped ? allDomainById : ownDomainById,
    loading: isSystemScoped ? loadingAllDomains : loadingOwnDomain,
    reload: isSystemScoped ? reloadAllDomains : reloadOwnDomain,
  }
}
